import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { translate, useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { differenceInDays } from 'date-fns';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';

import { Society } from 'types/Society';
import {
  BeneficiaryAgreedIncentiveOffer,
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentiveOfferPriceContext,
  IncentiveOfferSellerInfo,
  IncentiveOfferStatus,
  IncentiveOfferTimelineStep,
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';
import { setModal } from 'modules/_shared/redux/modalActions';
import {
  getBeneficiary,
  getBeneficiaries,
  getBeneficiaryIncentivesMarketContext,
} from 'modules/incentivesMarket/api/beneficiaryActions';
import { store } from 'redux/store';

import { IncentiveOfferSellerOfferCap } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';
import { IncentiveOfferDetailContextType } from '../types/IncentiveOfferDetailContextType';
import { incentiveOfferDetailInitValue } from '../constants/IncentiveOfferDetailInitValue';
import { getDefaultTimelineSteps } from '../constants/incentiveOfferTimelineSteps';
import { getIncentiveOfferOperations } from '../../../redux/incentiveOfferOperationActions';
import { getIncentiveOffer } from '../../../redux/incentiveOfferActions';
import AcceptIncentiveOfferModal from '../../../modals/AcceptIncentiveOffer';
import { AcceptIncentiveOfferModalProvider } from '../../../modals/AcceptIncentiveOffer/context/AcceptIncentiveOfferModalContext';
import getOfferReference from '../../../utils/getOfferReference';
import { getOfferStatus } from '../../../utils/getOfferStatus';
import { getPurchaseQuantityBounds } from '../../../utils/getPurchaseQuantityBounds';
import { useGetBeneficiaryIncentivesMarketContext } from '../../../hooks/useGetBeneficiaryIncentivesMarketContext';
import { buildSellerOfferCapFromContext } from '../../../utils/getSellerLimitsSummary';

const COMMISSION_RATE = 0.015;

export const IncentiveOfferDetailContext =
  createContext<IncentiveOfferDetailContextType>(incentiveOfferDetailInitValue);

export const IncentiveOfferDetailProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId, incentiveOfferId } = useParams<{
    societyId: string;
    incentiveOfferId: string;
  }>();
  const { t } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const beneficiaryId = queryParams.get('beneficiaryId');
  const beneficiaryQueryParam = beneficiaryId
    ? `?beneficiaryId=${beneficiaryId}`
    : '';

  const actualSociety: Society = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const [userBeneficiaryIds, setUserBeneficiaryIds] = useState<string[]>([]);

  const [beneficiaryBuyer, setBeneficiaryBuyer] =
    useState<IncentivesMarketBeneficiary | null>(null);
  const [buyerPlansLabel, setBuyerPlansLabel] = useState<string>('');

  const [offer, setOffer] = useState<IncentiveOffer | null>(null);
  const [acceptanceHistory, setAcceptanceHistory] = useState<
    BeneficiaryAgreedIncentiveOffer[]
  >([]);
  const [priceContext, setPriceContext] = useState<IncentiveOfferPriceContext>({
    currentOfferPrice: 0,
    lastTransactionPrice: 0,
    avgSellOffersPrice: 0,
    officialValuation: 0,
    userAvgBuyPrice: 0,
  });
  const [sellerInfo, setSellerInfo] = useState<IncentiveOfferSellerInfo | null>(
    null
  );
  const [incentiveOfferOperations, setIncentiveOfferOperations] = useState<
    IncentiveOfferOperation[]
  >([]);
  const [purchaseQuantity, setPurchaseQuantity] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const offerStatus = useMemo((): IncentiveOfferStatus => {
    if (!offer) return IncentiveOfferStatus.ACTIVE;
    return getOfferStatus(offer);
  }, [offer]);

  const isExpired = offerStatus === IncentiveOfferStatus.EXPIRED;

  const isOwnOffer = useMemo(() => {
    if (!offer) return false;
    const publisherId =
      typeof offer.beneficiary === 'string'
        ? offer.beneficiary
        : offer.beneficiary?._id;
    const publisherIdStr = publisherId ? String(publisherId) : '';

    if (beneficiaryId && publisherIdStr === beneficiaryId) {
      return true;
    }

    return Boolean(
      publisherIdStr && userBeneficiaryIds.includes(publisherIdStr)
    );
  }, [offer, beneficiaryId, userBeneficiaryIds]);

  const isBuyOffer = offer?.type === IncentiveOfferType.BUY;
  const shouldLoadSellerCap =
    isBuyOffer && !isOwnOffer && Boolean(beneficiaryId) && Boolean(societyId);

  const { data: sellerMarketContext, isLoading: isLoadingSellerCap } =
    useGetBeneficiaryIncentivesMarketContext({
      societyId,
      beneficiaryId: beneficiaryId || '',
      incentiveOfferId,
      enabled: shouldLoadSellerCap,
    });

  const offerReference = useMemo(() => getOfferReference(offer), [offer]);

  const offerRemainingUnits = useMemo(() => {
    if (!offer) return 0;
    return Math.max(0, offer.unitsCount.total - offer.unitsCount.agreed);
  }, [offer]);

  const sellerOfferCap = useMemo(():
    | IncentiveOfferSellerOfferCap
    | undefined => {
    if (!isBuyOffer || isOwnOffer) return undefined;

    return buildSellerOfferCapFromContext(
      sellerMarketContext.sellerOfferCap,
      offerRemainingUnits,
      sellerMarketContext.totalAvailableUnits
    );
  }, [
    isBuyOffer,
    isOwnOffer,
    sellerMarketContext.sellerOfferCap,
    sellerMarketContext.totalAvailableUnits,
    offerRemainingUnits,
  ]);

  const availableShares = useMemo(() => {
    if (!offer) return 0;

    if (isBuyOffer && !isOwnOffer && sellerOfferCap) {
      return sellerOfferCap.effectiveMax;
    }

    return offerRemainingUnits;
  }, [offer, isBuyOffer, isOwnOffer, sellerOfferCap, offerRemainingUnits]);

  const daysRemaining = useMemo(() => {
    if (!offer) return 0;
    const now = new Date();
    const endDate = new Date(offer.endDate);
    const days = differenceInDays(endDate, now);
    return Math.max(0, days);
  }, [offer]);

  const timelineSteps = useMemo((): IncentiveOfferTimelineStep[] => {
    if (!offer) return getDefaultTimelineSteps();

    const steps = getDefaultTimelineSteps();
    const agreedCount = offer.unitsCount.agreed;
    const approvedCount = offer.unitsCount.approved;
    const totalCount = offer.unitsCount.total;

    // Step 1: Published - always completed if offer exists
    steps[0].isCompleted = true;

    // Step 2: Waiting acceptance - completed if any shares agreed
    steps[1].isCompleted = agreedCount > 0;
    steps[1].isCurrent = agreedCount === 0;

    // Step 3: KYC verification - completed if shares approved
    steps[2].isCompleted = approvedCount > 0;
    steps[2].isCurrent = agreedCount > 0 && approvedCount === 0;

    // Step 4: Society validation - completed if all approved
    steps[3].isCompleted = approvedCount === totalCount && totalCount > 0;
    steps[3].isCurrent = approvedCount > 0 && approvedCount < totalCount;

    // Step 5: Transfer - completed if fully accepted
    steps[4].isCompleted = offerStatus === IncentiveOfferStatus.ACCEPTED;
    steps[4].isCurrent =
      approvedCount === totalCount &&
      offerStatus !== IncentiveOfferStatus.ACCEPTED;

    return steps;
  }, [offer, offerStatus]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePurchaseQuantityChange = useCallback(
    (quantity: number) => {
      const { min, max } = getPurchaseQuantityBounds(offer, availableShares);

      if (max <= 0) {
        setPurchaseQuantity(0);
        return;
      }

      const clampedQuantity = Math.max(min, Math.min(quantity, max));
      setPurchaseQuantity(clampedQuantity);
    },
    [availableShares, offer]
  );

  useEffect(() => {
    const { min, max } = getPurchaseQuantityBounds(offer, availableShares);

    setPurchaseQuantity((prev) => {
      if (max <= 0) return 0;
      if (offer && !offer.isPartialAmountEnabled) {
        return max;
      }
      if (prev === 0) return 0;
      return Math.max(min, Math.min(prev, max));
    });
  }, [offer, availableShares]);

  const handleBuySharesSuccess = useCallback(() => {
    setOffer((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        unitsCount: {
          ...prev.unitsCount,
          agreed: prev.unitsCount.agreed + purchaseQuantity,
        },
      };
    });
  }, [purchaseQuantity]);

  const handleBuyShares = useCallback(() => {
    if (!societyId || !offer || !user || !beneficiaryBuyer) return;

    const realSellerName =
      typeof offer.beneficiary === 'string'
        ? offer.beneficiary
        : offer.beneficiary?.name || '';

    const sellerName = offer.isSellerNameHidden
      ? t('HiddenName')
      : realSellerName;

    const unitClassName =
      typeof offer.unitClass === 'string' ? '' : offer.unitClass?.name || '';

    dispatch(
      setModal(
        <AcceptIncentiveOfferModalProvider
          offer={offer}
          beneficiaryBuyer={beneficiaryBuyer}
          purchaseQuantity={purchaseQuantity}
          societyId={societyId}
          offerReference={offerReference}
          sellerName={sellerName}
          buyerPlansLabel={buyerPlansLabel || unitClassName}
          onSuccess={handleBuySharesSuccess}
          size={sizes.LG}
        >
          <AcceptIncentiveOfferModal />
        </AcceptIncentiveOfferModalProvider>
      )
    );
  }, [
    offer,
    user,
    beneficiaryBuyer,
    societyId,
    purchaseQuantity,
    offerReference,
    dispatch,
    handleBuySharesSuccess,
    buyerPlansLabel,
    t,
  ]);

  const handleViewOtherOffers = useCallback(() => {
    history.push(`/ofertas-incentivos/${societyId}${beneficiaryQueryParam}`);
  }, [history, societyId, beneficiaryQueryParam]);

  const handleGoToOperation = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-incentivos/${societyId}/${operationId}${beneficiaryQueryParam}`
      );
    },
    [history, societyId, beneficiaryQueryParam]
  );

  useEffect(() => {
    if (!beneficiaryId) return;

    const fetchBeneficiaryBuyer = async () => {
      const loadedBeneficiary: IncentivesMarketBeneficiary | null =
        await store.dispatch(getBeneficiary(beneficiaryId));
      if (!loadedBeneficiary) {
        return;
      }
      setBeneficiaryBuyer(loadedBeneficiary);
    };

    fetchBeneficiaryBuyer();
  }, [beneficiaryId]);

  useEffect(() => {
    if (!beneficiaryBuyer?._id || !societyId) {
      setBuyerPlansLabel('');
      return;
    }

    let cancelled = false;

    const loadBuyerPlans = async () => {
      try {
        const context = await store.dispatch(
          getBeneficiaryIncentivesMarketContext({
            beneficiaryId: beneficiaryBuyer._id,
            societyId,
          })
        );
        if (cancelled) return;

        const label =
          context?.plans
            ?.map((plan: { planName?: string }) => plan.planName)
            .filter(Boolean)
            .join(', ') || '';

        setBuyerPlansLabel(label);
      } catch {
        if (!cancelled) {
          setBuyerPlansLabel('');
        }
      }
    };

    loadBuyerPlans();

    return () => {
      cancelled = true;
    };
  }, [beneficiaryBuyer?._id, societyId]);

  useEffect(() => {
    if (!societyId || !user?._id) {
      setUserBeneficiaryIds([]);
      return;
    }

    let cancelled = false;

    const fetchUserPartners = async () => {
      const res = await store.dispatch(
        getBeneficiaries(societyId, { userId: user._id, items: 100 })
      );
      if (cancelled) {
        return;
      }

      let ids =
        res?.data
          ?.map((p: IncentivesMarketBeneficiary) => String(p._id))
          .filter(Boolean) ?? [];

      if (beneficiaryId) {
        try {
          const cifContext: { beneficiaryIds?: string[] } =
            await store.dispatch(
              getBeneficiaryIncentivesMarketContext({
                beneficiaryId,
                societyId,
              })
            );
          if (cifContext?.beneficiaryIds?.length) {
            ids = [
              ...new Set([...ids, ...cifContext.beneficiaryIds.map(String)]),
            ];
          }
        } catch {
          // keep user-linked ids only
        }
      }

      setUserBeneficiaryIds(ids);
    };

    fetchUserPartners();

    return () => {
      cancelled = true;
    };
  }, [societyId, user?._id, beneficiaryId]);

  const actualSocietyId = actualSociety?._id;

  useEffect(() => {
    if (!societyId || !incentiveOfferId || !actualSocietyId) {
      return undefined;
    }

    let cancelled = false;

    const fetchOfferDetail = async () => {
      setIsLoading(true);

      try {
        let currentOffer: IncentiveOffer | null = null;
        try {
          currentOffer = await store.dispatch(
            getIncentiveOffer(incentiveOfferId)
          );
        } catch {
          currentOffer = null;
        }

        if (cancelled) {
          return;
        }

        if (
          currentOffer &&
          String(currentOffer.society) !== String(actualSocietyId)
        ) {
          setOffer(null);
          setAcceptanceHistory([]);
          setIncentiveOfferOperations([]);
          setSellerInfo(null);
          return;
        }

        setOffer(currentOffer);
        setAcceptanceHistory([]);

        const operationsResponse = await store.dispatch(
          getIncentiveOfferOperations({ incentiveOfferId })
        );

        if (cancelled) {
          return;
        }

        setIncentiveOfferOperations(operationsResponse?.data || []);
        setPriceContext({
          currentOfferPrice: currentOffer?.unitPrice || 0,
          lastTransactionPrice: 0,
          avgSellOffersPrice: 0,
          officialValuation: 0,
          userAvgBuyPrice: 0,
        });

        const sellerBeneficiaryId =
          typeof currentOffer?.beneficiary === 'string'
            ? currentOffer.beneficiary
            : currentOffer?.beneficiary?._id;

        if (sellerBeneficiaryId) {
          const sellerPartner: IncentivesMarketBeneficiary | null =
            await store.dispatch(getBeneficiary(sellerBeneficiaryId));

          if (cancelled) {
            return;
          }

          if (sellerPartner) {
            const beneficiaryShares = sellerPartner.sharesCount?.actual || 0;
            const totalSocietyShares = actualSociety?.sharesCount?.actual || 1;

            const isHidden = currentOffer?.isSellerNameHidden === true;

            let memberSince = '';
            if (!isHidden && sellerPartner.registerDate) {
              memberSince = new Date(sellerPartner.registerDate)
                .getFullYear()
                .toString();
            }

            setSellerInfo({
              beneficiaryId: sellerPartner._id,
              name: isHidden
                ? translate('HiddenName')
                : sellerPartner.name || '',
              image: isHidden ? '' : sellerPartner.image || '',
              memberSince,
              totalShares: beneficiaryShares,
              capitalPercentage:
                totalSocietyShares > 0
                  ? (beneficiaryShares / totalSocietyShares) * 100
                  : 0,
              previousOperations: 0,
              rating: 0,
            });
          } else {
            setSellerInfo(null);
          }
        } else {
          setSellerInfo(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching trading offer detail:', error);
          setOffer(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchOfferDetail();

    return () => {
      cancelled = true;
    };
  }, [societyId, incentiveOfferId, actualSocietyId]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        offer,
        offerStatus,
        offerReference,
        acceptanceHistory,
        incentiveOfferOperations,
        priceContext,
        timelineSteps,
        sellerInfo,
        availableShares,
        offerRemainingUnits,
        sellerOfferCap,
        daysRemaining,
        isAdmin,
        isExpired,
        isOwnOffer,
        userBeneficiaryIds,
        beneficiaryQueryParam,
        isLoading:
          isLoading ||
          !actualSociety ||
          (shouldLoadSellerCap && isLoadingSellerCap),
        hasPartnerBuyer: !!beneficiaryBuyer,
        purchaseQuantity,
        commissionRate: COMMISSION_RATE,
      },
      actions: {
        handleGoBack,
        handlePurchaseQuantityChange,
        handleBuyShares,
        handleViewOtherOffers,
        handleGoToOperation,
      },
    }),
    [
      societyId,
      actualSociety,
      offer,
      offerStatus,
      offerReference,
      acceptanceHistory,
      incentiveOfferOperations,
      priceContext,
      timelineSteps,
      sellerInfo,
      availableShares,
      offerRemainingUnits,
      sellerOfferCap,
      daysRemaining,
      isAdmin,
      isExpired,
      isOwnOffer,
      userBeneficiaryIds,
      beneficiaryQueryParam,
      isLoading,
      beneficiaryBuyer,
      purchaseQuantity,
      shouldLoadSellerCap,
      isLoadingSellerCap,
      handleGoBack,
      handlePurchaseQuantityChange,
      handleBuyShares,
      handleViewOtherOffers,
      handleGoToOperation,
    ]
  );

  return (
    <IncentiveOfferDetailContext.Provider value={contextValue}>
      {children}
    </IncentiveOfferDetailContext.Provider>
  );
};

export const useIncentiveOfferDetail = () => {
  const context = useContext(IncentiveOfferDetailContext);
  if (!context) {
    throw new Error(
      'useIncentiveOfferDetail must be used within a IncentiveOfferDetailProvider'
    );
  }
  return context;
};
